import React, { createContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "../lib/supabaseClient";
import { AuthContextValue, UserProfile } from "./types";
import { fetchUserProfile } from "./services/profileService";

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);

  async function loadUserProfile(userId: string): Promise<void> {
    const profile = await fetchUserProfile(userId);
    setUserProfile(profile);
  }

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, activeSession) => {
        setSession(activeSession);
        setUser(activeSession?.user ?? null);
        setAuthError(null);

        if (activeSession?.user) {
          const userId = activeSession.user.id;
          setTimeout(() => {
            loadUserProfile(userId).finally(() => setIsLoading(false));
          }, 0);
        } else {
          setUserProfile(null);
          setIsLoading(false);
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const contextValue: AuthContextValue = {
    session,
    user,
    userProfile,
    isLoading,
    authError,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
